/** Your data: everything Signals can answer from.
 *
 * "From your ERP" lists the nightly topics as the welcome guide labels them.
 * "Added by your team" is the spreadsheets people added: one card per topic
 * with who, when, how many rows, and what can be done with it. The page is
 * REST only; the server (backend/chainlit/data_upload_api.py) refuses admins.
 */
import {
  AlertTriangle,
  FileSpreadsheet,
  Link2,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  Trash2,
  Upload
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { chatHref } from '@/components/know-your-data/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

import AddDataWizard from './AddDataWizard';
import {
  DataPageData,
  UploadTopic,
  deleteUpload,
  fetchDataPage,
  formatCount,
  formatDay,
  formatWhen,
  refreshUpload,
  whoLabel
} from './api';

type View = { kind: 'list' } | { kind: 'add'; replace: UploadTopic | null };

function KindBadge({ topic }: { topic: UploadTopic }) {
  const sheet = topic.source === 'gsheet';
  const label = sheet ? 'GS' : topic.kind === 'csv' ? 'CSV' : 'XLSX';
  return (
    <div
      aria-hidden="true"
      className={
        'flex size-9 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ' +
        (sheet
          ? 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200'
          : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200')
      }
    >
      {label}
    </div>
  );
}

function StatusChip({ topic }: { topic: UploadTopic }) {
  if (topic.last_status === 'failed') {
    return (
      <span className="whitespace-nowrap rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
        Needs a look
      </span>
    );
  }
  if (!topic.in_chat) {
    return (
      <span className="whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        Out of the chat
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
      {topic.source === 'gsheet' ? 'Google Sheet' : 'Ready'}
    </span>
  );
}

function askQuestion(topic: UploadTopic): string {
  return `Give me a quick summary of "${topic.title}": what is in it and the main figures.`;
}

export default function DataPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DataPageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [working, setWorking] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UploadTopic | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchDataPage()
      .then(setData)
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Could not load this page.')
      );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDone = (topic: UploadTopic) => {
    setView({ kind: 'list' });
    load();
    toast.success(
      `${topic.title} is ready: ${formatCount(topic.row_count)} rows.`,
      {
        action: {
          label: 'Ask Signals',
          onClick: () => navigate(chatHref(askQuestion(topic)))
        }
      }
    );
  };

  const onRefresh = async (topic: UploadTopic) => {
    setWorking(topic.id);
    try {
      const r = await refreshUpload(topic.id);
      toast.success(
        `${r.topic.title} refreshed: ${formatCount(r.topic.row_count)} rows.`
      );
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'The sheet could not be read.'
      );
    } finally {
      setWorking(null);
      load();
    }
  };

  const onDelete = async (topic: UploadTopic) => {
    setConfirmDelete(null);
    setWorking(topic.id);
    try {
      await deleteUpload(topic.id);
      toast.success(`${topic.title} was removed.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'It could not be removed.');
    } finally {
      setWorking(null);
      load();
    }
  };

  if (view.kind === 'add') {
    return (
      <AddDataWizard
        replace={view.replace}
        onDone={onDone}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
        <div className="rounded-xl border border-border px-4 py-6 text-center text-sm text-muted-foreground">
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      </div>
    );
  }

  const full = data.limits.topics >= data.limits.max_topics;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-7 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-xl space-y-1.5">
          <h1 className="text-2xl font-semibold">Your data</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            Everything Signals can answer from. Add a spreadsheet to ask about
            numbers your ERP does not hold: targets, budgets, competitor prices,
            headcount, anything.
          </p>
        </div>
        <Button
          type="button"
          disabled={full}
          title={
            full
              ? `Your company already has ${data.limits.max_topics} added topics.`
              : undefined
          }
          onClick={() => setView({ kind: 'add', replace: null })}
        >
          <Plus aria-hidden="true" />
          Add data
        </Button>
      </div>

      <section
        aria-labelledby="erp-topics"
        className="flex flex-col gap-3 rounded-2xl border border-border p-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <h2 id="erp-topics" className="text-base font-semibold">
              From your ERP
            </h2>
            {data.erp.length > 0 && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
                Updated every night
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {data.erp.length === 0
              ? 'No ERP topics yet'
              : `${data.erp.length} topic${data.erp.length === 1 ? '' : 's'}` +
                (data.erp.some((t) => t.updated_through)
                  ? ` · as of ${formatDay(
                      data.erp
                        .map((t) => t.updated_through)
                        .filter(Boolean)
                        .sort()
                        .slice(-1)[0]
                    )}`
                  : '')}
          </span>
        </div>
        {data.erp.length > 0 ? (
          <ul className="grid list-none gap-x-7 gap-y-1 p-0 md:grid-cols-2">
            {data.erp.map((t) => (
              <li
                key={t.id}
                className="flex items-start gap-3 border-b border-border/60 py-2.5 last:border-b-0 md:[&:nth-last-child(2)]:border-b-0"
              >
                <span
                  aria-hidden="true"
                  className="mt-[7px] size-[7px] shrink-0 rounded-full bg-primary"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{t.label}</div>
                  {t.summary && (
                    <div className="text-[13px] leading-5 text-muted-foreground">
                      {t.summary}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            Your ERP is not connected yet. You can still add spreadsheets below
            and ask about them.
          </p>
        )}
      </section>

      <section aria-labelledby="team-topics" className="flex flex-col gap-3.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="team-topics" className="text-base font-semibold">
            Added by your team
          </h2>
          <span className="text-xs text-muted-foreground">
            {data.limits.topics} of {data.limits.max_topics} · visible to
            everyone in your company
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {data.uploads.map((topic) => {
            const busy = working === topic.id;
            const failed = topic.last_status === 'failed';
            return (
              <article
                key={topic.id}
                className={
                  'flex flex-col gap-3 rounded-2xl border p-4 ' +
                  (failed
                    ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                    : 'border-border')
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <KindBadge topic={topic} />
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold">
                        {topic.title}
                      </h3>
                      <span className="block truncate text-xs text-muted-foreground">
                        {formatCount(topic.row_count)} rows ·{' '}
                        {topic.columns.length} columns
                        {topic.file_name ? ` · ${topic.file_name}` : ''}
                      </span>
                    </div>
                  </div>
                  <StatusChip topic={topic} />
                </div>

                {failed && topic.last_status_note ? (
                  <p className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-200">
                    <AlertTriangle
                      className="mt-0.5 size-4 shrink-0"
                      aria-hidden="true"
                    />
                    <span>
                      {topic.last_status_note} The previous rows are kept.
                    </span>
                  </p>
                ) : (
                  <>
                    {topic.description && (
                      <p className="text-[13px] leading-5 text-foreground/80">
                        {topic.description}
                      </p>
                    )}
                    {(topic.grain_text || topic.columns.length > 0) && (
                      <p className="text-xs leading-[18px] text-muted-foreground">
                        {topic.grain_text
                          ? `One row is ${topic.grain_text}`
                          : ''}
                        {topic.grain_text && topic.columns.length > 0
                          ? ' · '
                          : ''}
                        {topic.columns.length > 0
                          ? `columns: ${topic.columns.map((c) => c.name).join(', ')}`
                          : ''}
                      </p>
                    )}
                  </>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {topic.source === 'gsheet' ? 'Linked' : 'Added'} by{' '}
                    {whoLabel(topic.created_by_email)} ·{' '}
                    {formatDay(topic.created_at)}
                    {topic.updated_at !== topic.created_at
                      ? ` · updated ${formatWhen(topic.updated_at)}`
                      : ''}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy || !topic.in_chat}
                      onClick={() => navigate(chatHref(askQuestion(topic)))}
                    >
                      <MessageSquare aria-hidden="true" />
                      Ask about it
                    </Button>
                    {topic.source === 'gsheet' ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void onRefresh(topic)}
                      >
                        <RefreshCw
                          className={busy ? 'animate-spin' : ''}
                          aria-hidden="true"
                        />
                        Refresh
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => setView({ kind: 'add', replace: topic })}
                      >
                        <Upload aria-hidden="true" />
                        New version
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove ${topic.title}`}
                      disabled={busy}
                      onClick={() => setConfirmDelete(topic)}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}

          {!full && (
            <button
              type="button"
              onClick={() => setView({ kind: 'add', replace: null })}
              className="flex min-h-36 flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-border p-4 text-center hover:border-primary/60 hover:bg-primary/5"
            >
              <span className="flex items-center gap-2 text-primary">
                <FileSpreadsheet className="size-5" aria-hidden="true" />
                <Link2 className="size-5" aria-hidden="true" />
              </span>
              <span className="text-sm font-semibold">Add a spreadsheet</span>
              <span className="max-w-xs text-xs leading-5 text-muted-foreground">
                Excel, CSV or a Google Sheet link. No template: your columns are
                kept exactly as they are.
              </span>
            </button>
          )}
        </div>
      </section>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              Signals stops answering from it and the rows are deleted. A
              dashboard tile built on it will go empty. You can add the file
              again any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && void onDelete(confirmDelete)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
